import { type FileData, FileIconMap } from "@qendo/profile/files";
import { useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Button } from "~/components/Button";
import {
    DropdownContent,
    DropdownItem,
    DropdownItemIcon,
    DropdownItemTitle,
    DropdownRoot,
    DropdownTrigger,
} from "~/components/dropdown";
import { ImageBox } from "~/components/layout/gallery/ImageBox";
import { BaseText } from "~/components/layout/text";
import { QendoIcon } from "~/components/QendoIcon";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { setWidthOnLayout, titleWidth } from "~/util/layout";
import { isImage, isPhotograph } from "~/util/qendo/media";
import { RenameFile } from "./RenameFile";

export type FileListingItemProps = {
    item: FileData;
    onRequestRemove?: (id: string) => void;
    onRequestRestore?: (id: string) => void;
    disabled?: boolean;
};

const BUTTON_SIZE = 26;
const CONTAINER_GAP = 24;
const BODY_GAP = 12;

export const FileListingItem = ({ item, onRequestRemove, onRequestRestore, disabled }: FileListingItemProps) => {
    const { colours } = useTheme();
    const { cannot } = useProfile();
    const [preview, setPreview] = useState(false);
    const [rename, setRename] = useState(false);
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);

    const can_remove = filled(onRequestRemove);
    const can_restore = filled(onRequestRestore);
    const can_preview = isImage(item);
    const can_rename = isPhotograph(item);

    const { center } = useMemo(() => titleWidth(width, CONTAINER_GAP, BUTTON_SIZE), [width]);
    const title_width = useMemo(() => Math.floor(center) - BUTTON_SIZE - BODY_GAP, [center]);

    return (
        <>
            <View style={styles.container} onLayout={onLayout}>
                {can_restore && (
                    <Button
                        icon="plus"
                        variant="success"
                        size="sm"
                        disabled={disabled}
                        onPress={() => onRequestRestore(item.id)}
                        style={styles.button}
                    />
                )}
                {can_remove && (
                    <Button
                        icon="minus"
                        variant="destructive"
                        size="sm"
                        disabled={disabled}
                        onPress={() => onRequestRemove(item.id)}
                        style={styles.button}
                    />
                )}
                <View style={[styles.body, { width: center }]}>
                    <QendoIcon name={FileIconMap[item.category]} size={BUTTON_SIZE} color={colours.icon.secondary} />
                    <BaseText numberOfLines={1} size={"sm"} style={{ width: title_width }}>
                        {item.name}
                    </BaseText>
                </View>
                {can_preview && (
                    <DropdownRoot>
                        <DropdownTrigger>
                            <Button icon="ellipsis" variant="secondary" size="sm" style={styles.button} />
                        </DropdownTrigger>
                        <DropdownContent>
                            <DropdownItem key="file-preview" onSelect={() => setPreview(true)}>
                                <DropdownItemTitle>Preview</DropdownItemTitle>
                                <DropdownItemIcon icon="view-on" ios={{ name: "eye" }} />
                            </DropdownItem>
                            {can_rename && (
                                <DropdownItem
                                    key="file-rename"
                                    onSelect={() => setRename(true)}
                                    disabled={cannot("update", "photos")}
                                >
                                    <DropdownItemTitle>Rename</DropdownItemTitle>
                                    <DropdownItemIcon icon="edit" ios={{ name: "pencil" }} />
                                </DropdownItem>
                            )}
                        </DropdownContent>
                    </DropdownRoot>
                )}
            </View>
            {can_preview && preview && <ImageBox image={item} onRequestClose={() => setPreview(false)} />}
            {can_rename && rename && <RenameFile file={item} onRequestClose={() => setRename(false)} />}
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "flex-start",
        flex: 1,
        gap: CONTAINER_GAP,
    },
    button: {
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
    },
    body: {
        flexDirection: "row",
        alignItems: "center",
        gap: BODY_GAP,
    },
});
